import { AppStreamSDK } from "@utils/AppStreamSDK";
import { useTranslation } from "react-i18next";
import { useFilesNotificationStore } from "@hooks/useNotificationStore";
import { IFileExplorerApi, NotificationItem } from "@amzn/aws-euc-ui";
import { useNotification } from "@hooks/useNotification";
import { useMemo } from "react";
import { EmbedConstants } from "@amzn/photon-portal-embed-sdk";
import { ABORTED_AS2_LISTFILE_MSG } from "@constants/toolbar";
import { MimeType } from "@constants/common";
import { ToolbarMetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

// https://pubs.opengroup.org/onlinepubs/007908799/xbd/glossary.html#tag_004_000_114
const INVALID_NAME_CHARS = ["/", "\0"];

const UNKNOWN_ERROR_REASON = "Unknown error reason";
const getGeneralFileErrorReason = (error?: { errorMessage?: string }) =>
    [
        "The operation was cancelled",
        "The operation was aborted",
        "Path not found",
        "The file already exists",
        "The operation failed",
        "Invalid argument",
        "The operation is not supported",
        "Unknown command",
        // 1. File explorer is actually not ready yet.
        // 2. Refresh page while uploading a file
        "File explorer is not ready",
    ].includes(error?.errorMessage)
        ? error.errorMessage
        : UNKNOWN_ERROR_REASON;

const INVALID_FILE_NAME = "Invalid file name";
const INVALID_FILE_NAME_PREFIX = "Rename command cannot contain";

export const useFilesApis = (appStreamSDK: AppStreamSDK) => {
    const { t } = useTranslation();
    const log = useGetLogger();

    const notificationList = useFilesNotificationStore(
        (store) => store.notificationList
    );

    const addNotification = useFilesNotificationStore(
        (store) => store.addNotification
    );

    const removeNotification = useFilesNotificationStore(
        (store) => store.removeNotification
    );

    const DELETE_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.deleteFile.content"),
        removeNotification: removeNotification,
    });

    const LIST_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.listFile.content"),
        removeNotification: removeNotification,
    });

    const RENAME_DIRECTORY_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.renameDirectory.content"),
        removeNotification: removeNotification,
    });

    const DOWNLOAD_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.downloadFile.content"),
        removeNotification: removeNotification,
    });

    const UPLOAD_FILE_ERROR: NotificationItem = useNotification({
        header: t("notification.fail.uploadFile.content"),
        removeNotification: removeNotification,
    });

    return useMemo<IFileExplorerApi>(
        () => ({
            // CreateDirectory error handled internally: https://tiny.amazon.com/107hpu5e4/codeamazpackAWSEblob865dsrc
            createDirectory: (path) => {
                return appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_CREATE_FOLDER, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.CREATE_DIRECTORY_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.CREATE_DIRECTORY_ERROR
                        );
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.CREATE_DIRECTORY_ERROR_REASON,
                            {
                                reason: getGeneralFileErrorReason(error),
                            }
                        );
                        throw error;
                    });
            },
            deleteFile: (path) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_DELETE_FILE, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.DELETE_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.DELETE_FILE_ERROR
                        );
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.DELETE_FILE_ERROR_REASON,
                            {
                                reason: getGeneralFileErrorReason(error),
                            }
                        );
                        addNotification(DELETE_FILE_ERROR);
                    }),
            list: (path) =>
                appStreamSDK
                    .listFiles(path)
                    .then((result) => {
                        log.publishNumericMetric(
                            ToolbarMetricName.LIST_FILES_SUCCESS,
                            result?.length ?? 0
                        );
                        return result;
                    })
                    .catch((error) => {
                        // The in-session root folder path is /TemporaryFiles. The files toolbar item fetches the data from / first by default
                        // and sends a new request to get the file list under /TemporaryFiles. This is causing a "The operation was aborted" error,
                        // which is expected. Since this is expected behavior, we will ignore the error to reduce noise on the dashboard.
                        if (
                            path === "/" &&
                            error.errorMessage === ABORTED_AS2_LISTFILE_MSG
                        ) {
                            log.logMessage(
                                `Initial FileExplorer.ListFiles API call pointing to the "/" folder is aborted.`
                            );
                            return;
                        }
                        log.publishCounterMetric(
                            ToolbarMetricName.LIST_FILES_ERROR
                        );
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.LIST_FILES_ERROR_REASON,
                            {
                                reason: getGeneralFileErrorReason(error),
                            }
                        );
                        // https://sim.amazon.com/issues/LOWA-9174
                        if (error.errorCode !== 400) {
                            // Only show ListFile API error once since we are using 3 retries with useQuery (to avoid multiple errors on screen from one fetch)
                            if (
                                !notificationList.find(
                                    (item) =>
                                        item.header === LIST_FILE_ERROR.header
                                )
                            ) {
                                addNotification(LIST_FILE_ERROR);
                            }
                        }
                        return [];
                    }),
            renameDirectory: (src, dest) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_RENAME_FILE, {
                        remotePath: src,
                        newPath: dest,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.RENAME_DIRECTORY_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.RENAME_DIRECTORY_ERROR
                        );
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.RENAME_DIRECTORY_ERROR_REASON,
                            {
                                reason: getGeneralFileErrorReason(error),
                            }
                        );
                        addNotification(RENAME_DIRECTORY_ERROR);
                    }),
            // RenameFile error handled internally: https://tiny.amazon.com/oxaj7lvc/codeamazpackAWSEblob865dsrc
            renameFile: (originalPath, newPath) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_RENAME_FILE, {
                        remotePath: originalPath,
                        newPath: newPath,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.RENAME_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.RENAME_FILE_ERROR
                        );
                        let errorReason = getGeneralFileErrorReason(error);
                        if (
                            error?.errorMessage?.startsWith(
                                INVALID_FILE_NAME_PREFIX
                            )
                        ) {
                            errorReason = INVALID_FILE_NAME;
                        }
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.RENAME_FILE_ERROR_REASON,
                            {
                                reason: errorReason,
                            }
                        );
                        throw error;
                    }),
            retrieveFile: (path) =>
                appStreamSDK
                    .performActionPromise(EmbedConstants.METHOD_DOWNLOAD_FILE, {
                        remotePath: path,
                    })
                    .then((result) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.DOWNLOAD_FILE_SUCCESS
                        );
                        return result;
                    })
                    .catch((error) => {
                        log.publishCounterMetric(
                            ToolbarMetricName.DOWNLOAD_FILE_ERROR
                        );
                        log.publishMetricWithDimensions(
                            ToolbarMetricName.DOWNLOAD_FILE_ERROR_REASON,
                            {
                                reason: getGeneralFileErrorReason(error),
                            }
                        );
                        addNotification(DOWNLOAD_FILE_ERROR);
                    }),
            storeFile: async (file, dir, onProgress) => {
                onProgress(0);

                try {
                    await appStreamSDK.performActionPromise(
                        EmbedConstants.METHOD_UPLOAD_FILE,
                        {
                            file: file,
                            remotePath: dir,
                        }
                    );
                    log.publishNumericMetric(
                        ToolbarMetricName.UPLOAD_FILE_SUCCESS,
                        file?.size ?? 0,
                        {
                            type: file?.type ?? MimeType.OCTET_STREAM, // Unknown
                        }
                    );
                } catch (error) {
                    log.publishCounterMetric(
                        ToolbarMetricName.UPLOAD_FILE_ERROR
                    );
                    log.publishMetricWithDimensions(
                        ToolbarMetricName.UPLOAD_FILE_ERROR_REASON,
                        {
                            reason: getGeneralFileErrorReason(error),
                        }
                    );
                    log.publishMetricWithDimensions(
                        ToolbarMetricName.UPLOAD_FILE_ERROR_TYPE,
                        {
                            type: file?.type ?? MimeType.OCTET_STREAM, // Unknown
                        },
                        file?.size ?? 0
                    );
                    addNotification(UPLOAD_FILE_ERROR);
                }

                onProgress(file.size);
            },
            validateName: (name) => {
                if (
                    INVALID_NAME_CHARS.some((invalidChar) =>
                        name.includes(invalidChar)
                    )
                ) {
                    return t(
                        "toolbar.item.files.name.validation.invalidCharacter"
                    );
                }
                return null;
            },
        }),
        [appStreamSDK, JSON.stringify(notificationList)]
    );
};
